import React from 'react';
import { IconRoot, IconRootProps } from './style/Icon.style';

export type IconProps = React.SVGProps<SVGSVGElement> &
  IconRootProps & {
    svgIconComponent?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  };

/**
 * Displays SVG images.
 *
 * 2 api:
 *
 * 1. Prop 'iconSrc' to pass the svg as an url that will rendered
 * as a background image.
 * 2. Prop 'svgIconComponent' to render the <svg /> element in the 'DOM'.
 *
 * Prefer the 1st method use the 2nd one only if you have to dynamically
 * change some 'svg' attribute, e.g. fill, stroke, style etc...
 *
 * @example
 * ```javascript
 * // This svg is imported as a component, see:
 * // https://create-react-app.dev/docs/adding-images-fonts-and-files/#adding-svgs
 * import Icon from '...';
 * // <PlaceholderIcon /> -> renders '<svg />'
 * import PlaceHolderIcon from 'images/icons/placeholder-icon.svg?react';
 *
 * // Renders '<div className="i-set-the-size-of-the-svg"><svg /></div>'.
 * // You can override the styles of the 'div' using 'style(Icon)`[other styles]`'.
 * const WrappedComp = <Icon svgIconComponent={PlaceHolderIcon} iconSize="16px" />
 * ```
 */
const Icon: React.FC<IconProps> = ({
  fill = 'currentcolor',
  svgIconComponent: OptionalSvgIconComponent,
  iconSize,
  iconHeight,
  iconMinWidth,
  iconSrc,
  className,
  margin,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  stroke,
  inlineIconSrc,
  viewBox,
  display,
  'aria-hidden': ariaHidden,
}) => (
  <IconRoot
    iconSize={iconSize}
    iconMinWidth={iconMinWidth}
    className={className}
    iconSrc={iconSrc}
    iconHeight={iconHeight}
    margin={margin}
    marginTop={marginTop}
    marginRight={marginRight}
    marginBottom={marginBottom}
    marginLeft={marginLeft}
    inlineIconSrc={inlineIconSrc}
    aria-hidden={ariaHidden}
    display={display}
  >
    {OptionalSvgIconComponent && (
      <OptionalSvgIconComponent
        fill={fill}
        stroke={stroke}
        {...(viewBox && { viewBox })}
      />
    )}
  </IconRoot>
);

export default Icon;
